// Initialize the loadedScripts object to track the script's state and callbacks
const loadedScripts = {};

/**
 * Dynamically loads a JavaScript file and tracks its state (inProgress, loaded, callbacks).
 *
 * @param {string} url - The URL of the JavaScript file to load.
 * @returns {Promise} - Resolves when the script is loaded, rejects on failure.
 */
export function loadScript(url) {
    // Check if the script is already fully loaded
    if (loadedScripts[url]?.loaded) {
        return Promise.resolve();
    }

    // Check if the script is currently in progress
    if (loadedScripts[url]?.inProgress) {
        // Return the existing Promise instead of creating a new one
        return loadedScripts[url].promise;
    }

    // Create a new Promise and mark the script as in progress
    const scriptPromise = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = url;
        script.async = true; // Non-blocking execution
        script.onload = () => {
            loadedScripts[url] = { inProgress: false, loaded: true }; // Mark as loaded
            resolve(); // Resolve the Promise
        };
        script.onerror = () => {
            loadedScripts[url] = { inProgress: false, loaded: false }; // Reset state on failure
            reject(new Error(`Failed to load script: ${url}`));
        };
        document.head.appendChild(script); // Append the script to <head>
    });

    // Save the Promise state to the tracker
    loadedScripts[url] = {
        inProgress: true,
        loaded: false,
        promise: scriptPromise,
    };

    return scriptPromise;
}

/**
 * Dynamically loads a JavaScript file with a callback (for backward compatibility).
 *
 * @param {string} url - The URL of the JavaScript file to load.
 * @param {function} callback - A function to execute once the script is loaded.
 */
export function loadScriptWithCallback(url, callback) {
    loadScript(url)
        .then(() => {
            if (callback) callback(); // Call the callback when the script is loaded
        })
        .catch((error) => {
            console.error(error); // Handle script loading errors
        });
}
